import React, { useRef, useState, useEffect } from "react";
import {
  Button,
  Col,
  Collapse,
  Form,
  message,
  Row,
  Typography,
} from "antd";
import ConsultantForm from "./CommonComponents/ConsultantForm";
import PatientProfile from "./CommonComponents/PatientProfile";
import ClinicalImpression from "./CommonComponents/ClinicalImpression";
import HistoryIllness from "./CommonComponents/HistoryIllness";
import Medication from "./CommonComponents/Medication";
import Nebulization from "./CommonComponents/Nebulization";
import Vitals from "./CommonComponents/Vitals";
import CourseOfStay from "./CommonComponents/CourseOfStay";
import { CloseOutlined } from "@ant-design/icons";
import {
  getListInvestigationFormApi,
  updateInvestigationFormByIdApi,
  getPatientDetailsApi,
  printFormApi,
  shareIpFormApi,
  submitInvestigationFormApi,
} from "../../../services/forms.services";
import {
  getLabelTagsWithFormDescription,
  getPastMedicalHistoryFormSummary,
  printFormSummary,
} from "./formsKeys";
import {
  knownAllergiesOptions,
  pastMedicalOptions,
} from "../../IpEMR/Options/historyOptions";
import btnStyles from "../../../styles/Custom/Button.module.css";
import useQuery from "../../../hooks/useQuery";
import NoDataPage from "../../NoDataPages/NoDataPage";
import assessmentIcon from "../../../assets/images/nurseIcons/assessment.svg";
import FormContentLoader from "./FormContentLoader";
import ScaledHtmlPreview from "../../ui/ScaledHtmlPreview";
import ShareModal from "../Forms/DischargeSummary/ShareModal";
import ReviewModal from "./ReviewForms/ReviewModal";
import {
  isWithinEditWindow,
  normalizeRecordList,
} from "./CommonComponents/formRecords";
import "./CommonComponents/FormDocument.css";
import "./FormsIp.css";

const InvestigationReferralForm = ({ patientData, role }) => {
  const [form] = Form.useForm();
  const formRef = useRef(null);

  const [referralFormData, setReferralFormData] = useState(null);
  const [editable, setEditable] = useState(false);
  const [openShare, setOpenShare] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [details, setDetails] = useState(null);
  const [displayForm, setDisplayForm] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [recordList, setRecordList] = useState([]);
  const [openHistory, setOpenHistory] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const refreshPage = () => setRefresh((prev) => !prev);
  const isActive = useQuery()?.status === "current";
  const isReceptionist = role === "receptionist";
  const canEditForm = isActive && !isReceptionist && editable;

  const loadRecordDisplay = async (record, isCancelled = () => false) => {
    if (!record?.id) {
      if (!isCancelled()) setDisplayForm(null);
      return;
    }
    try {
      const previewRes = await printFormApi(
        `form_type=investigation&form_id=${record.id}`
      );
      if (!isCancelled()) setDisplayForm(previewRes?.data?.data ?? null);
    } catch (err) {
      console.log(err);
      if (!isCancelled()) setDisplayForm(null);
    }
  };

  useEffect(() => {
    if (!patientData?.admission?.id || !patientData?.doctor_intial_assessment) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    const isCancelled = () => cancelled;
    setIsLoading(true);
    setDisplayForm(null);

    const loadForm = async () => {
      try {
        const [listRes, detailsRes] = await Promise.all([
          getListInvestigationFormApi(patientData.admission.id).catch((err) => {
            if (err?.response?.status === 404) return null;
            throw err;
          }),
          getPatientDetailsApi(patientData.admission.id),
        ]);
        if (cancelled) return;

        const list = normalizeRecordList(listRes?.data);
        setRecordList(list);
        setDetails(detailsRes?.data);

        if (isNewForm) {
          setReferralFormData(null);
          setEditable(isActive && !isReceptionist);
          return;
        }

        const openId = referralFormData?.data?.id;
        const record =
          list.find((row) => row?.id === openId) ??
          list[list.length - 1] ??
          null;

        setReferralFormData(record ? { data: record } : null);
        if (isReceptionist || !isActive) {
          setEditable(false);
        } else {
          setEditable(record?.id ? false : true);
        }

        await loadRecordDisplay(record, isCancelled);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [patientData?.admission?.id, patientData?.doctor_intial_assessment, refresh, isReceptionist, isActive]);

  const handleNewForm = () => {
    setIsNewForm(true);
    setReferralFormData(null);
    form.resetFields();
    setDisplayForm(null);
    setEditable(true);
  };

  const handleHistoryAction = (record, isEdit) => {
    form.resetFields();
    setIsNewForm(false);
    setOpenHistory(false);
    setReferralFormData(record ? { data: record } : null);
    setEditable(isEdit && !isReceptionist && isActive);
    setDisplayForm(null);
    setIsLoading(true);
    loadRecordDisplay(record).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    form.setFieldsValue(referralFormData?.data?.data);
  }, [referralFormData?.data?.data]);

  const onFinish = (values) => {
    const formData = {
      patient: details?.data?.patient,
      diagnosis: details?.data?.diagnosis,
      medications: details?.data?.medications,
      functional_status: details?.data?.functional_status,
      vitals: details?.data?.vitals,
      nebulization: details?.data?.nebulization,
      past_medical_history: details?.data?.present_illness
        ? getPastMedicalHistoryFormSummary(
            pastMedicalOptions,
            details?.data?.present_illness
          )
        : "",
      known_allergies: details?.data?.present_illness
        ? getLabelTagsWithFormDescription(
            knownAllergiesOptions,
            details?.data?.present_illness
          )
        : "",
      past_surgical_history: details?.data?.present_illness?.surgical_history
        ? details?.data?.present_illness?.surgical_history
        : null,
      past_surgical_history_details: details?.data?.present_illness
        ?.surgical_history_details
        ? details?.data?.present_illness?.surgical_history_details
        : null,
      ...values,
    };
    const formId = referralFormData?.data?.id;
    if (!formId && !isNewForm && recordList?.length) {
      message.error(
        "Could not tell which form is open. Please reopen it from History and try again."
      );
      return;
    }
    if (formId) {
      updateInvestigationFormByIdApi(formId, { data: formData })
        .then((res) => {
          message.success(res?.data?.message);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    } else {
      submitInvestigationFormApi({
        ip_admission: patientData?.admission?.id,
        data: formData,
      })
        .then((res) => {
          message.success(res?.data?.message);
          setIsNewForm(false);
          setEditable(false);
          refreshPage();
        })
        .catch((err) => message.error(err?.response?.data?.message));
    }
  };

  const commonStyle = {
    // background: "#fff",
    boxShadow:
      "0px 2px 4px -2px rgba(16, 24, 40, 0.06), 0px 4px 8px -2px rgba(16, 24, 40, 0.1)",
  };
  return (
    <>
      {!patientData?.doctor_intial_assessment ? (
        <Row
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "80vh",
          }}
        >
          <Col span={24} style={{ textAlign: "center" }}>
            <NoDataPage
              title={"Initial Assessment Not Yet Submitted "}
              subtitle={"Kindly, submit initial assessment first"}
              image={assessmentIcon}
            />
          </Col>
        </Row>
      ) : (
        <FormContentLoader
          loading={isLoading}
          tip="Loading investigation referral form..."
        >
        <div style={{ position: "relative" }}>
          <div className="forms-ip-header">
            <div className="forms-ip-header-lead">
              <Typography.Title level={5} className="forms-ip-header-title">
                Investigation Referral Form
              </Typography.Title>
              {referralFormData?.data?.id &&
                !editable &&
                isActive &&
                !isReceptionist && (
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={handleNewForm}
                  >
                    New Form
                  </Button>
                )}
            </div>
            {referralFormData?.data?.id && (
              <div className="forms-ip-header-actions">
                {recordList?.length > 0 && (
                  <Button
                    className="ds-btn ds-btn--ghost"
                    onClick={() => setOpenHistory(true)}
                  >
                    History
                  </Button>
                )}
                {isActive &&
                  !["receptionist"]?.includes(role) &&
                  isWithinEditWindow(referralFormData?.data?.created_at) && (
                    <Button
                      className="ds-btn ds-btn--ghost"
                      onClick={() => setEditable(true)}
                    >
                      Edit
                    </Button>
                  )}
                <Button
                  className="ds-btn ds-btn--ghost"
                  onClick={() =>
                    printFormSummary(
                      "investigation",
                      referralFormData?.data?.id
                    )
                  }
                >
                  Print
                </Button>
                <Button
                  className="ds-btn ds-btn--primary"
                  onClick={() => setOpenShare(true)}
                >
                  Share
                </Button>
                {canEditForm && (
                  <CloseOutlined onClick={() => setEditable(false)} />
                )}
              </div>
            )}
          </div>
          {canEditForm ? (
            <Form
              form={form}
              ref={formRef}
              layout="vertical"
              onFinish={onFinish}
              className="forms-ip-shell forms-ip-scroll-shell"
            >
              <Collapse
                expandIconPosition="right"
                className="custom-collapse"
                defaultActiveKey={["1", "2", "3", "4", "5", "6", "7"]}
              >
                <Collapse.Panel
                  header="Patient Profile"
                  style={commonStyle}
                  key="1"
                >
                  <PatientProfile data={patientData?.admission} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Consultants"
                  style={commonStyle}
                  key="2"
                >
                  <ConsultantForm form={form} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Clinical Impression/ Diagnosis"
                  style={commonStyle}
                  key="3"
                >
                  <ClinicalImpression form={form} details={details} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="History of Present Illness"
                  style={commonStyle}
                  key="4"
                >
                  <HistoryIllness
                    form={form}
                    patientData={patientData?.admission}
                    data={details?.data?.present_illness}
                  />
                </Collapse.Panel>
                <br />
                <CourseOfStay form={form} />
                <Collapse.Panel header="Medication" style={commonStyle} key="5">
                  <Medication form={form} details={details} />
                </Collapse.Panel>
                <Collapse.Panel
                  header="Nebulization"
                  style={commonStyle}
                  key="6"
                >
                  <Nebulization form={form} details={details} />
                </Collapse.Panel>

                <Collapse.Panel
                  header="Vitals"
                  style={{ ...commonStyle, marginBottom: "40px" }}
                  key="7"
                >
                  <Vitals form={form} details={details} />
                </Collapse.Panel>
              </Collapse>
              <div
                className="ip-fixed-form-footer"
                style={{
                  position: "fixed",
                  bottom: 0,
                  left: 0,

                  width: "100%",
                  background: "#fff",
                  paddingTop: "10px",
                  paddingRight: "16px",
                  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.1)",
                  textAlign: "right",
                  zIndex: 1000, // Ensure it stays above other elements
                }}
              >
                {isActive && !["receptionist"]?.includes(role) && (
                  <Form.Item>
                    <Button
                      htmlType="submit"
                      type="primary"
                      className={btnStyles.lgBtn}
                    >
                      {referralFormData?.data?.id ? "Update" : "Submit"}
                    </Button>
                  </Form.Item>
                )}
              </div>
            </Form>
          ) : isReceptionist && !referralFormData?.data?.id ? (
            <NoDataPage
              title="Investigation Referral Form Not Available"
              subtitle="Investigation referral form has not been submitted yet"
              image={assessmentIcon}
            />
          ) : displayForm ? (
            <ScaledHtmlPreview
              html={displayForm}
              designWidth={900}
              className="ip-form-html-preview"
            />
          ) : (
            <NoDataPage
              title="Investigation Referral Form Not Available"
              subtitle="Investigation referral form is not available for discharged patients"
              image={assessmentIcon}
            />
          )}
        </div>
        </FormContentLoader>
      )}
      {openHistory && (
        <ReviewModal
          open={openHistory}
          setOpen={setOpenHistory}
          reviewList={recordList}
          isActive={isActive && !isReceptionist}
          title="Investigation Referral History"
          onShowClick={(record) => handleHistoryAction(record, false)}
        />
      )}
      {openShare && (
        <ShareModal
          openModal={openShare}
          setOpenModal={setOpenShare}
          patientData={patientData}
          title="Share Investigation Referral Form"
          documentLabel="Investigation referral form"
          shareApi={(payload) =>
            shareIpFormApi("investigation", referralFormData?.data?.id, payload)
          }
        />
      )}
    </>
  );
};

export default InvestigationReferralForm;
